/**
 * /l/:slug — V2 anonymous ordering experience.
 *
 * V2 is intentionally V1 with ONE surgical UI change: the "The Record"
 * step's product-card grid is swapped for a compact text-option list
 * plus a single large hero using the SAME image assets, pricing
 * behaviour, quantity area, "Your Order" sidebar, header, progress
 * navigation and Continue button. Everything else is byte-identical
 * to V1 because we still mount the same `ProductConfigWizard` in
 * `visual` mode — we simply forward a new optional prop
 * `recordStepVariant="text-hero"` down to `FlowDrivenBuilder`. When
 * that prop is omitted (i.e. every existing V1 landing page) the
 * render is unchanged.
 *
 * The draft/auth/order-redeem glue is the same shape as V1's
 * `LandingPage.jsx`. We only diverge on the small subset of the
 * markup that renders the record step, so the surrounding chrome
 * (Configure Your Vinyl header, progress rail, quantity block, Your
 * Order summary, price, Continue) stays identical.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { Loader2, ShieldCheck } from 'lucide-react';
import QuickOrderFlow from '../components/QuickOrderFlow';
import ProductConfigWizard from '../components/ProductConfigWizard';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../contexts/TranslationContext';
import { API_BASE } from '../lib/apiBase';
import { resolvePublicCurrency } from '../lib/publicCurrency';
import { usePublicTheme } from '../hooks/usePublicTheme';
import '@owntune/theme/theme.css';
import '@owntune/ui/styles.css';
import { PublicUIProvider } from '@owntune/ui';

const API = `${API_BASE}/api`;
// Reuse V1's storage key so a visitor who lands on V2, bounces to
// login and returns picks up their pending draft without ceremony.
const PENDING_DRAFT_KEY = 'pending_landing_page_draft';

export default function LandingPageV2() {
    const { slug } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { login, user } = useAuth();
    const { t, language } = useTranslation();

    usePublicTheme();

    const [page, setPage] = useState(null);
    const [bundles, setBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [pendingPayload, setPendingPayload] = useState(null);
    const [authOpen, setAuthOpen] = useState(false);
    const [authMode, setAuthMode] = useState('register');
    const [authEmail, setAuthEmail] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [authName, setAuthName] = useState('');
    const [authBusy, setAuthBusy] = useState(false);

    // Re-fetch page + bundles when `language` changes so translated
    // title/description/bundle copy render without a page reload.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const langQ = language && language !== 'en' ? `?lang=${encodeURIComponent(language)}` : '';
                const res = await axios.get(`${API}/public/landing-pages/${slug}${langQ}`);
                if (cancelled) return;
                setPage(res.data);
                try {
                    const bLangQ = language && language !== 'en' ? `&lang=${encodeURIComponent(language)}` : '';
                    const bRes = await axios.get(
                        `${API}/product-config/bundles?product_family=${res.data.product_family}${bLangQ}`,
                    );
                    if (!cancelled) setBundles(bRes.data || []);
                } catch {
                    if (!cancelled) setBundles([]);
                }
            } catch (err) {
                if (!cancelled) setError(err.response?.data?.detail || 'Landing page not found');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug, language]);

    const effectiveCurrency = useMemo(
        () => resolvePublicCurrency(searchParams.get('currency'), page?.default_currency || 'EUR'),
        [searchParams, page?.default_currency],
    );

    useEffect(() => {
        if (!user) return;
        const raw = localStorage.getItem(PENDING_DRAFT_KEY);
        if (!raw) return;
        try {
            const draft = JSON.parse(raw);
            redeemDraft(draft);
        } catch {
            localStorage.removeItem(PENDING_DRAFT_KEY);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleQuickOrderComplete = (specs, priceEstimate, meta) => {
        const draft = {
            landing_page_slug: page.slug,
            landing_page_id: page.id,
            product_family: page.product_family,
            bundle_id: meta?.bundle?.id,
            bundle_title: meta?.bundle?.title,
            quantity: meta?.quantity,
            title: meta?.title || page.title,
            specs,
            price_estimate: priceEstimate,
            source: 'quick_order',
            quote_comment: meta?.quote_comment || null,
            created_at: new Date().toISOString(),
        };
        localStorage.setItem(PENDING_DRAFT_KEY, JSON.stringify(draft));
        setPendingPayload(draft);
        if (user) redeemDraft(draft);
        else setAuthOpen(true);
    };

    const handleWizardComplete = (specs, priceEstimate) => {
        const draft = {
            landing_page_slug: page.slug,
            landing_page_id: page.id,
            product_family: page.product_family,
            quantity: parseInt(specs?.quantity, 10) || undefined,
            title: page.title,
            specs,
            price_estimate: priceEstimate,
            source: page.starting_step === 'expert_setup' ? 'expert' : 'guided_v2',
            created_at: new Date().toISOString(),
        };
        localStorage.setItem(PENDING_DRAFT_KEY, JSON.stringify(draft));
        setPendingPayload(draft);
        if (user) redeemDraft(draft);
        else setAuthOpen(true);
    };

    const redeemDraft = async (draft) => {
        try {
            const specs = { ...(draft.specs || {}) };
            const flowId = specs._flow_id;
            const flowVersion = specs._flow_version;
            const flowSelections = specs._flow_selections;
            // Capture the quote-gate hint BEFORE we delete it so we can
            // branch the submit path to /api/orders/request-quote. The
            // backend also revalidates, so this is just a routing hint.
            const requiresQuote = !!specs._requires_quote;
            delete specs._flow_id;
            delete specs._flow_version;
            delete specs._flow_selections;
            delete specs._requires_quote;
            delete specs._quote_gated_skus;

            let vinyl_specs = null;
            const isFlow = Boolean(flowId && flowSelections);

            if (isFlow) {
                vinyl_specs = specs;
            } else if (draft.product_family === 'vinyl') {
                const weightRaw = specs.vinyl_weight ?? specs.vinyl_weight_g ?? '140';
                const weightInt = parseInt(String(weightRaw).replace('g', ''), 10) || 140;
                vinyl_specs = {
                    ...specs,
                    format: specs.format || '12"',
                    quantity: draft.quantity,
                    speed: specs.speed || '33 RPM',
                    color: specs.color || 'Black',
                    vinyl_weight_g: weightInt,
                    test_pressings: parseInt(String(specs.test_pressings ?? 0), 10) || 0,
                    _bundle_id: draft.bundle_id,
                    _bundle_title: draft.bundle_title,
                };
            } else if (draft.bundle_id) {
                vinyl_specs = {
                    ...specs,
                    format: specs.format,
                    quantity: draft.quantity,
                    _bundle_id: draft.bundle_id,
                    _bundle_title: draft.bundle_title,
                };
            }
            // Scrub client-only hint fields that leaked into vinyl_specs.
            if (vinyl_specs) {
                delete vinyl_specs._requires_quote;
                delete vinyl_specs._quote_gated_skus;
            }
            const body = {
                title: draft.title,
                product_type: draft.product_family || 'vinyl',
                quantity: draft.quantity,
                vinyl_specs,
                order_type: 'new_production',
                ...(isFlow ? {
                    config_flow_id: flowId,
                    config_flow_version: flowVersion,
                    config_selections: flowSelections,
                } : {}),
            };
            const token = localStorage.getItem('token');
            // Quote-gate branch (same contract as LandingPage.jsx) —
            // routes to the dedicated request-quote endpoint which
            // creates the order directly in Quote status with no bogus
            // zero totals.
            if (requiresQuote) {
                const comment = (draft.quote_comment || '').trim();
                const res = await axios.post(
                    `${API}/orders/request-quote`,
                    { order: body, comment: comment || null },
                    { headers: { Authorization: `Bearer ${token}` } },
                );
                localStorage.removeItem(PENDING_DRAFT_KEY);
                toast.success(`Quote requested — order ${res.data.order_number}`);
                navigate(`/orders/${res.data.id}`);
                return;
            }
            const res = await axios.post(`${API}/orders`, body, {
                headers: { Authorization: `Bearer ${token}` },
            });
            localStorage.removeItem(PENDING_DRAFT_KEY);
            toast.success(`Order ${res.data.order_number} created`);
            navigate(`/orders/${res.data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create order after login');
        }
    };

    const handleAuthSubmit = async () => {
        if (!authEmail || !authPassword) { toast.error(t('auth.fill_email_password', 'Please fill in your email and password')); return; }
        setAuthBusy(true);
        try {
            if (authMode === 'register') {
                if (!authName.trim()) { toast.error(t('auth.enter_name', 'Please enter your name')); setAuthBusy(false); return; }
                await axios.post(`${API}/auth/register`, {
                    email: authEmail, password: authPassword, name: authName.trim(),
                });
            }
            await login(authEmail, authPassword);
            setAuthOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Authentication failed');
        } finally {
            setAuthBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background p-6">
                <Card className="max-w-md w-full">
                    <CardContent className="py-10 text-center space-y-3">
                        <h1 className="text-xl font-bold text-foreground">{t('landing.not_found', 'Landing page not found')}</h1>
                        <p className="text-sm text-muted-foreground">{error}</p>
                        <Button onClick={() => navigate('/login')} variant="outline">{t('common.go_to_signin', 'Go to sign in')}</Button>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!page) return null;

    const urlBundleId = searchParams.get('bundle');
    const initialBundleId = urlBundleId || page.default_bundle_id || null;
    const startingStep = initialBundleId ? 'quantity' : 'packages';

    const wizardMode =
        page.starting_step === 'guided_setup' ? 'visual'
        : page.starting_step === 'expert_setup' ? 'expert'
        : null;

    return (
        <PublicUIProvider active>
        <div className="min-h-screen bg-background" data-testid="landing-page-v2">
            <main className="max-w-none mx-auto px-4 sm:px-6 lg:px-10 pt-6 pb-10 space-y-4">
                <div className="text-center max-w-3xl mx-auto">
                    <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight">
                        {page.title}
                    </h1>
                    {page.description && (
                        <p className="text-sm text-muted-foreground mt-1.5">{page.description}</p>
                    )}
                </div>

                {wizardMode ? (
                    <ProductConfigWizard
                        productFamily={page.product_family}
                        mode={wizardMode}
                        onModeChange={() => { /* locked */ }}
                        onComplete={handleWizardComplete}
                        currencyOverride={effectiveCurrency}
                        recordStepVariant="text-hero"
                        showPrices={page.show_prices !== false}
                    />
                ) : bundles.length === 0 ? (
                    <Card className="max-w-md mx-auto">
                        <CardContent className="py-8 text-center text-sm text-slate-500">
                            No bundles configured for this product family yet.
                        </CardContent>
                    </Card>
                ) : (
                    <QuickOrderFlow
                        bundles={bundles}
                        productFamily={page.product_family}
                        initialBundleId={initialBundleId}
                        startingStep={startingStep}
                        requireTitle={true}
                        initialTitle=""
                        onComplete={handleQuickOrderComplete}
                        submitLabel={t('landing.continue_workspace', 'Continue to your workspace')}
                        compact={true}
                        currencyOverride={effectiveCurrency}
                        showPrices={page.show_prices !== false}
                    />
                )}

                <p className="text-[11px] text-center text-muted-foreground pt-1">
                    {page.show_prices !== false
                        ? t('landing.powered_footer', "Powered by Owntune · Configure freely — save when you're ready · Pricing in {currency}", { currency: effectiveCurrency })
                        : t('landing.powered_footer_no_price', "Powered by Owntune · Configure freely — save when you're ready")}
                </p>
            </main>

            <Dialog open={authOpen} onOpenChange={setAuthOpen}>
                <DialogContent className="max-w-md" data-testid="landing-v2-auth-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 font-display">
                            <ShieldCheck className="h-5 w-5 text-amber-500" />
                            {page.show_prices === false
                                ? t('landing.auth_title_price', 'See your vinyl price')
                                : t('landing.auth_title', 'Create your production workspace')}
                        </DialogTitle>
                        <DialogDescription>
                            {page.show_prices === false ? (
                                <>{t('landing.auth_desc_price', 'Your configuration is ready! Sign in or create a free account to view your personalized price and continue with your order.')}</>
                            ) : pendingPayload?.bundle_title ? (
                                <>{t('landing.auth_desc_with_bundle', 'Your {bundle} ({qty} copies) draft is ready. Register or sign in to save it.', { bundle: pendingPayload.bundle_title, qty: pendingPayload.quantity })}</>
                            ) : (
                                <>{t('landing.auth_desc', 'Sign in or create an account to save your draft and continue.')}</>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex gap-2 p-1 bg-slate-100 rounded-lg">
                        <button
                            onClick={() => setAuthMode('register')}
                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                                authMode === 'register' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600'
                            }`}
                            data-testid="landing-v2-auth-mode-register"
                        >
                            {t('landing.new_here', 'New here')}
                        </button>
                        <button
                            onClick={() => setAuthMode('login')}
                            className={`flex-1 py-2 text-sm font-medium rounded-md transition-all ${
                                authMode === 'login' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-600'
                            }`}
                            data-testid="landing-v2-auth-mode-login"
                        >
                            {t('landing.returning_customer', 'Returning customer')}
                        </button>
                    </div>

                    <div className="space-y-3 py-2">
                        {authMode === 'register' && (
                            <div>
                                <Label htmlFor="lp-v2-auth-name">{t('auth.your_name_lower', 'Your name')}</Label>
                                <Input
                                    id="lp-v2-auth-name"
                                    value={authName}
                                    onChange={(e) => setAuthName(e.target.value)}
                                    placeholder={t('auth.full_name_placeholder', 'Full name')}
                                    className="mt-1"
                                    data-testid="landing-v2-auth-name"
                                />
                            </div>
                        )}
                        <div>
                            <Label htmlFor="lp-v2-auth-email">{t('auth.email_field', 'Email')}</Label>
                            <Input
                                id="lp-v2-auth-email"
                                type="email"
                                value={authEmail}
                                onChange={(e) => setAuthEmail(e.target.value)}
                                placeholder={t('auth.email_label_at_placeholder', 'you@label.com')}
                                className="mt-1"
                                data-testid="landing-v2-auth-email"
                            />
                        </div>
                        <div>
                            <Label htmlFor="lp-v2-auth-pass">{t('common.password', 'Password')}</Label>
                            <Input
                                id="lp-v2-auth-pass"
                                type="password"
                                value={authPassword}
                                onChange={(e) => setAuthPassword(e.target.value)}
                                className="mt-1"
                                data-testid="landing-v2-auth-password"
                            />
                        </div>
                        <Button
                            className="w-full"
                            onClick={handleAuthSubmit}
                            disabled={authBusy}
                            data-testid="landing-v2-auth-submit"
                        >
                            {authBusy && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
                            {authMode === 'register'
                                ? (page.show_prices === false ? t('landing.create_and_see_price', 'Create account & see price') : t('landing.create_and_continue', 'Create account & continue'))
                                : (page.show_prices === false ? t('landing.signin_and_see_price', 'Sign in & see price') : t('landing.signin_and_continue', 'Sign in & continue'))}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
        </PublicUIProvider>
    );
}
